import { useState } from "react";
import type { Key } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { CloseIcon } from "@/assets/icons/general/Close";
import { InputHeader } from "@/components/ui/inputs/shared/InputHeader";
import { Drawer } from "@/components/ui/overlays/Drawer/Drawer";

import type { DropdownProps } from "./Dropdown";
import { DropdownButton } from "./DropdownButton";
import { DropdownListBox } from "./DropdownListBox";

export const MobileDropdown = <TKey extends Key = Key>(props: DropdownProps<TKey>) => {
  const { label, items, placeholder, value, selectionMode, variant, customTrigger } = props;
  const { t } = useTranslation();

  const selectedItems = items.filter((item) =>
    selectionMode === "multiple" ? value.includes(item.id) : item.id === value,
  );

  const [isOpen, setIsOpen] = useState(false);

  const onChange: DropdownProps<TKey>["onChange"] = (val: typeof value) => {
    if (selectionMode === "multiple") {
      props.onChange(val as TKey[]);
    } else {
      props.onChange(val as TKey);
      setIsOpen(false);
    }
  };

  const listboxProps = {
    ...props,
    onChange,
  } as DropdownProps<TKey>;

  return (
    <div>
      <InputHeader label={label} />
      <Drawer
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        trigger={
          customTrigger ?? (
            <DropdownButton variant={variant}>
              <span className={selectedItems.length > 0 ? "" : "text-text-default-tertiary"}>
                {selectedItems.length > 0 ? selectedItems.map((item) => item.label).join(", ") : placeholder}
              </span>
            </DropdownButton>
          )
        }
      >
        {(close) => (
          <div className="flex h-screen flex-col">
            <DropdownListBox
              header={
                <div className="px-4 pt-3">
                  <InputHeader
                    label={label}
                    rightContent={
                      <button
                        type="button"
                        onClick={close}
                        aria-label={t("ui.dropdown.closeAlt")}
                        className="flex-shrink-0"
                      >
                        <CloseIcon className="h-6 w-6 text-interactive-icon-idle" />
                      </button>
                    }
                  />
                </div>
              }
              {...listboxProps}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
};
